import { CardContainer } from '../CardContainer';

interface TrendIndicatorProps {
  value: number;
}

const TrendIndicator = ({ value }: TrendIndicatorProps) => {
  const isPositive = value >= 0;
  const formattedValue = Math.abs(value).toFixed(1);

  return (
    <div className={`flex items-center gap-1 text-sm font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
      <svg
        className={`h-4 w-4 ${isPositive ? '' : 'rotate-180'}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
      </svg>
      <span>{formattedValue}%</span>
    </div>
  );
};

const ExternalLinkIcon = () => (
  <svg
    className="h-4 w-4 text-gray-400"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
  </svg>
);

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: string;
  trend?: number;
  color?: 'blue' | 'green' | 'orange' | 'purple';
  onClick?: () => void;
  showChevron?: boolean;
  className?: string;
}

const colorClasses = {
  blue: 'bg-blue-50 text-primary',
  green: 'bg-green-50 text-green-600',
  orange: 'bg-orange-50 text-orange-500',
  purple: 'bg-purple-50 text-purple-600',
};

export const StatCard = ({
  title,
  value,
  subtitle,
  icon,
  trend,
  color = 'blue',
  onClick,
  showChevron,
  className: extraClassName,
}: StatCardProps) => {
  return (
    <CardContainer
      className={[
        onClick ? 'cursor-pointer hover:shadow-md hover:bg-gray-50 transition-all duration-200' : '',
        extraClassName,
      ].filter(Boolean).join(' ')}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <span className="material-symbols-outlined text-2xl">{icon}</span>
        </div>
        <div className="flex items-center gap-2">
          {trend !== undefined && <TrendIndicator value={trend} />}
          {showChevron && <ExternalLinkIcon />}
        </div>
      </div>
      <div className="mt-4">
        <p className="text-sm font-medium text-slate-500">{title}</p>
        <p className="text-2xl font-bold text-[#101822] mt-1">{value}</p>
        {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
      </div>
    </CardContainer>
  );
};
